import { motion } from "framer-motion";
import { Instagram, Music2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";

const slugify = (s = "") =>
  s.toString().toLowerCase().trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const Artists = () => {
  const { content } = useContent();
  const artists = content?.artists || [];

  const goToContact = () => {
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="kuenstler"
      data-testid="artists-section"
      className="relative bg-[#050505] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Roster" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              03 — Künstler / Roster
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl max-w-3xl">
              Stimmen aus dem <br />
              <span className="text-glow-red text-white">Asphalt.</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            Eine Auswahl unseres aktuellen Rosters. Für vollständige Liste & Bookings — direkter Kontakt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
          {artists.map((a, i) => (
            <motion.div
              key={a.name + i}
              data-testid={`artist-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: i * 0.08, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
              className="group relative aspect-[3/4] overflow-hidden border border-white/[0.06]"
            >
              <Link
                to={`/kuenstler/${a.slug || slugify(a.name)}`}
                className="absolute inset-0 z-10"
                data-testid={`artist-link-${i}`}
                aria-label={a.name}
              />
              {a.image && (
                <img
                  src={a.image}
                  alt={a.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000 pointer-events-none"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-[#ff0033]/15 via-transparent to-transparent" />

              {/* Top-left role badge */}
              {a.role && (
                <div
                  data-testid={`artist-role-${i}`}
                  className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 px-2.5 py-1 border border-[#ff0033]/50 bg-[#ff0033]/15 text-[9px] tracking-[0.3em] uppercase text-white font-bold backdrop-blur-md shadow-[0_0_18px_rgba(255,0,51,0.35)]"
                >
                  <span className="red-led" />
                  {a.role}
                </div>
              )}

              {/* Top-right Instagram badge — clickable */}
              {a.instagram_url && (
                <a
                  href={a.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`artist-instagram-${i}`}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-5 right-5 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 hover:border-[#ff0033]/60 px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-white hover:shadow-[0_0_16px_rgba(255,0,51,0.35)] transition-all"
                >
                  <Instagram size={12} />
                  {a.instagram_handle || "Instagram"}
                </a>
              )}

              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 z-10">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2">
                  {String(i + 1).padStart(2, "0")} / Roster
                </div>
                <div className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
                  {a.name}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-2">
                  {a.tag}
                </div>

                {/* Music promo link */}
                {a.music_url && (
                  <a
                    href={a.music_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`artist-music-${i}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-5 inline-flex items-center gap-3 border-t border-white/15 pt-4 text-[11px] tracking-[0.25em] uppercase font-bold text-white hover:text-[#ff0033] transition-colors group/btn"
                  >
                    <Music2 size={14} className="text-[#ff0033]" />
                    <span>{a.music_label || "Aktuelle Songs"}</span>
                    <ExternalLink
                      size={12}
                      className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform"
                    />
                  </a>
                )}
              </div>

              <div className="absolute top-0 left-0 right-0 h-px bg-[#ff0033] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 shadow-[0_0_12px_#ff0033]" />
            </motion.div>
          ))}

          {/* Call-to-action DU? tile */}
          <motion.button
            type="button"
            data-testid="artist-cta-du"
            onClick={goToContact}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: 0.15, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="group relative aspect-[3/4] overflow-hidden border border-white/[0.08] bg-[#080808] flex flex-col items-center justify-center text-center p-8 transition-all duration-500 hover:border-[#ff0033]/50 hover:shadow-[0_0_40px_-10px_rgba(255,0,51,0.5)] hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,0,51,0.12),transparent_65%)] opacity-70 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
              backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 5px)"
            }} />
            <div className="relative z-10 flex flex-col items-center">
              <div className="red-led mb-6" />
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#ff0033] font-bold">Next Drop</div>
              <div className="font-display font-black uppercase text-7xl md:text-8xl text-white tracking-tighter mt-4 text-glow-red group-hover:text-glow-red-strong transition-all">
                DU?
              </div>
              <div className="mt-6 text-sm text-zinc-400 max-w-[240px] leading-relaxed">
                Könntest der nächste Name hier sein. Schick deine Bewerbung.
              </div>
              <div className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-bold text-white border-t border-white/15 pt-4 group-hover:border-[#ff0033]/60 transition-colors">
                <span>Jetzt bewerben</span>
                <span className="text-[#ff0033] group-hover:translate-x-1 transition-transform duration-300">→</span>
              </div>
            </div>
            <div className="absolute top-0 left-0 right-0 h-px bg-[#ff0033] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 shadow-[0_0_12px_#ff0033]" />
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Artists;
